
/**
 * Development Mode Detector - Ultra Silent Mode
 * Minimal logging for completely silent development experience
 */

interface DevelopmentConfig {
  isProduction: boolean;
  isDevelopment: boolean;
  allowExternalCalls: boolean;
  enableMonitoring: boolean;
  enablePerformanceTracking: boolean;
  enableHealthChecks: boolean;
  enableAnalytics: boolean;
  fontStrategy: 'system' | 'external';
}

class DevelopmentModeDetector {
  private config: DevelopmentConfig;
  private hasLoggedStatus = false;

  constructor() {
    this.config = this.detectEnvironment();
    // Ultra-silent mode - no startup logging
  }

  private detectEnvironment(): DevelopmentConfig {
    const isViteProd = import.meta.env.PROD;
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    
    // Enhanced production detection for Dialgia.com
    const isRealProduction = (
      hostname.includes('dialgia.com') ||
      hostname.includes('dialgia.co.uk') ||
      (isViteProd && protocol === 'https:' && !hostname.includes('localhost'))
    );
    
    // Development detection
    const isDevelopment = (
      hostname.includes('localhost') || 
      hostname.includes('127.0.0.1') ||
      hostname.includes('lovableproject.com') ||
      hostname.includes('preview') ||
      (!isRealProduction && !isViteProd)
    );

    // Log environment detection for debugging
    console.log(`🌍 Environment Detection:`, {
      hostname,
      protocol,
      isViteProd,
      isRealProduction,
      isDevelopment
    });

    return {
      isProduction: isRealProduction,
      isDevelopment,
      allowExternalCalls: true, // Always allow external calls (both dev and prod need them)
      enableMonitoring: isRealProduction,
      enablePerformanceTracking: false, // Always disabled to prevent conflicts
      enableHealthChecks: isRealProduction,
      enableAnalytics: isRealProduction,
      fontStrategy: isDevelopment ? 'system' : 'external'
    };
  }

  getConfig(): DevelopmentConfig {
    return { ...this.config };
  }

  shouldBlockExternalCalls(): boolean {
    return !this.config.allowExternalCalls;
  }

  shouldDisableMonitoring(): boolean {
    return !this.config.enableMonitoring;
  }

  shouldDisablePerformanceTracking(): boolean {
    return !this.config.enablePerformanceTracking;
  }

  shouldDisableHealthChecks(): boolean {
    return !this.config.enableHealthChecks;
  }

  shouldDisableAnalytics(): boolean {
    return !this.config.enableAnalytics;
  }

  useSystemFonts(): boolean {
    return this.config.fontStrategy === 'system';
  }
}

export const developmentDetector = new DevelopmentModeDetector();
export type { DevelopmentConfig };
